class StarmistProduct extends HTMLElement {
  connectedCallback() {
    this.variantControls = this.querySelectorAll('[data-variant-control]');
    this.variantData = this.querySelector('[data-product-variants]');

    if (!this.variantData) return;

    try {
      this.variants = JSON.parse(this.variantData.textContent);
    } catch (error) {
      return;
    }

    this.productInformation = this.querySelector('.product__information--sticky');
    if (this.productInformation) {
      this.updateStickyInformation = this.updateStickyInformation.bind(this);
      this.informationResizeObserver = new ResizeObserver(this.updateStickyInformation);
      this.informationResizeObserver.observe(this.productInformation);
      window.addEventListener('resize', this.updateStickyInformation);
      this.updateStickyInformation();
    }

    this.variantControls.forEach((control) => {
      control.addEventListener('change', () => {
        this.updateVariant(control.value);
      });
    });

    this.querySelectorAll('[data-product-quantity]').forEach((input) => {
      input.addEventListener('change', () => {
        const quantity = Math.max(1, Number.parseInt(input.value, 10) || 1);
        this.querySelectorAll('[data-product-form-quantity]').forEach((formInput) => {
          formInput.value = quantity;
        });
      });
    });
  }

  disconnectedCallback() {
    this.informationResizeObserver?.disconnect();
    window.removeEventListener('resize', this.updateStickyInformation);
  }

  updateStickyInformation() {
    if (!this.productInformation) return;

    const availableHeight = window.innerHeight - 48;
    const canStick = this.productInformation.scrollHeight <= availableHeight;
    this.productInformation.classList.toggle('product__information--sticky-active', canStick);
  }

  updateVariant(variantId) {
    const variant = this.variants.find((item) => String(item.id) === String(variantId));
    if (!variant) return;

    this.variantControls.forEach((control) => {
      if (control.matches('input[type="radio"]')) {
        control.checked = String(control.value) === String(variant.id);
      } else {
        control.value = String(variant.id);
      }
    });

    this.querySelectorAll('[data-product-form-variant]').forEach((input) => {
      input.value = variant.id;
    });

    const price = this.querySelector('[data-product-price]');
    const compareAtPrice = this.querySelector('[data-product-compare-price]');
    const addButton = this.querySelector('[data-add-to-cart]');
    const addButtonLabel = addButton?.querySelector('[data-button-label]');
    const stickyAddButton = this.querySelector('[data-sticky-add-to-cart]');
    const stickyLabel = this.querySelector('[data-sticky-label]');
    const stickyPrice = this.querySelector('[data-sticky-price]');

    if (price) price.textContent = this.formatMoney(variant.price);

    if (compareAtPrice) {
      compareAtPrice.textContent = variant.compare_at_price
        ? this.formatMoney(variant.compare_at_price)
        : '';
      compareAtPrice.hidden = !variant.compare_at_price || variant.compare_at_price <= variant.price;
    }

    if (addButton) {
      addButton.disabled = !variant.available;
      if (addButtonLabel) addButtonLabel.textContent = variant.available
        ? addButton.dataset.availableLabel
        : addButton.dataset.soldOutLabel;
    }

    if (stickyAddButton) {
      stickyAddButton.disabled = !variant.available;
    }

    if (stickyLabel && stickyAddButton) {
      stickyLabel.textContent = variant.available
        ? stickyAddButton.dataset.availableLabel
        : stickyAddButton.dataset.soldOutLabel;
    }

    if (stickyPrice) {
      stickyPrice.textContent = this.formatMoney(variant.price);
    }

    this.updateInventory(variant);
    this.querySelector('starmist-product-gallery')?.showMedia(variant.featured_media_id);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    this.dispatchEvent(
      new CustomEvent('starmist:variant-change', {
        bubbles: true,
        detail: { variant },
      }),
    );
  }

  updateInventory(variant) {
    const inventory = this.querySelector('[data-product-inventory]');
    const inventoryText = inventory?.querySelector('[data-product-inventory-text]');
    if (!inventory || !inventoryText) return;

    const quantity = Number(variant.inventory_quantity) || 0;
    const threshold = Number(inventory.dataset.lowStockThreshold) || 5;
    const isManaged = Boolean(variant.inventory_management);
    const showQuantity = inventory.dataset.showQuantity === 'true';
    let state = 'in-stock';
    let text = inventory.dataset.inStockLabel;

    if (!variant.available) {
      state = 'sold-out';
      text = inventory.dataset.soldOutLabel;
    } else if (isManaged && quantity > 0 && quantity <= threshold) {
      state = 'low-stock';
      text = inventory.dataset.lowStockLabel;
    }

    if (variant.available && showQuantity && isManaged && quantity > 0) {
      text = `${text}: ${quantity}`;
    }

    inventory.classList.remove(
      'product__inventory--in-stock',
      'product__inventory--low-stock',
      'product__inventory--sold-out',
    );
    inventory.classList.add(`product__inventory--${state}`);
    inventoryText.textContent = text;
  }

  formatMoney(cents) {
    const currency = this.dataset.currency || 'USD';
    const formattedPrice = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency,
    }).format(cents / 100);

    return `${formattedPrice} ${currency}`;
  }
}

if (!customElements.get('starmist-product')) {
  customElements.define('starmist-product', StarmistProduct);
}

class StarmistProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.thumbnailTrack = this.querySelector('.product-gallery__thumbnails');
    this.thumbnails = Array.from(this.querySelectorAll('[data-gallery-thumbnail]'));
    this.previousButton = this.querySelector('[data-gallery-previous]');
    this.nextButton = this.querySelector('[data-gallery-next]');
    this.status = this.querySelector('[data-gallery-status]');
    this.currentIndex = 0;
    this.programmaticTargetIndex = null;
    this.pendingAnnouncement = false;

    if (!this.track || this.slides.length === 0) return;

    this.onScroll = this.onScroll.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.showPrevious = () => this.showSlide(this.currentIndex - 1);
    this.showNext = () => this.showSlide(this.currentIndex + 1);
    this.onThumbnailClick = (event) => {
      event.preventDefault();
      this.showSlide(Number(event.currentTarget.dataset.galleryIndex));
    };
    this.onResize = () => this.showSlide(this.currentIndex, false, 'auto');

    this.previousButton?.addEventListener('click', this.showPrevious);
    this.nextButton?.addEventListener('click', this.showNext);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('keydown', this.onKeydown);
    this.thumbnails.forEach((thumbnail) => thumbnail.addEventListener('click', this.onThumbnailClick));

    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this.track);
    this.classList.add('is-enhanced');
    this.updateControls(false);
  }

  disconnectedCallback() {
    this.previousButton?.removeEventListener('click', this.showPrevious);
    this.nextButton?.removeEventListener('click', this.showNext);
    this.track?.removeEventListener('scroll', this.onScroll);
    this.track?.removeEventListener('keydown', this.onKeydown);
    this.thumbnails?.forEach((thumbnail) => thumbnail.removeEventListener('click', this.onThumbnailClick));
    this.resizeObserver?.disconnect();
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
  }

  onScroll() {
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = requestAnimationFrame(() => {
      const nearestIndex = this.slides.reduce((closestIndex, slide, index) => {
        const currentDistance = Math.abs(slide.offsetLeft - this.track.scrollLeft);
        const closestDistance = Math.abs(this.slides[closestIndex].offsetLeft - this.track.scrollLeft);
        return currentDistance < closestDistance ? index : closestIndex;
      }, 0);
      let nextIndex = nearestIndex;
      let announce = true;

      if (this.programmaticTargetIndex !== null) {
        const targetIndex = this.programmaticTargetIndex;
        const targetOffset = this.slides[targetIndex].offsetLeft;
        const hasArrived = Math.abs(this.track.scrollLeft - targetOffset) <= 2;

        if (hasArrived) {
          nextIndex = targetIndex;
          announce = this.pendingAnnouncement;
          this.programmaticTargetIndex = null;
          this.pendingAnnouncement = false;
        } else if (nearestIndex !== this.currentIndex) {
          nextIndex = this.currentIndex + Math.sign(targetIndex - this.currentIndex);
          announce = false;
        } else {
          return;
        }
      }

      nextIndex = Math.min(this.slides.length - 1, Math.max(0, nextIndex));
      if (nextIndex === this.currentIndex) return;

      this.currentIndex = nextIndex;
      this.updateControls(announce, this.programmaticTargetIndex !== null ? 'auto' : 'smooth');
    });
  }

  onKeydown(event) {
    if (event.target !== this.track) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    this.showSlide(this.currentIndex + (event.key === 'ArrowRight' ? 1 : -1));
  }

  showMedia(mediaId) {
    if (!mediaId) return;

    const mediaIndex = this.slides.findIndex(
      (slide) => String(slide.dataset.mediaId) === String(mediaId),
    );

    if (mediaIndex >= 0) this.showSlide(mediaIndex);
  }

  showSlide(index, announce = true, requestedBehavior = 'smooth') {
    const nextIndex = Math.min(this.slides.length - 1, Math.max(0, Number(index) || 0));
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = prefersReducedMotion ? 'auto' : requestedBehavior;

    if (behavior === 'auto' || nextIndex === this.currentIndex) {
      this.programmaticTargetIndex = null;
      this.pendingAnnouncement = false;
      this.currentIndex = nextIndex;
      this.track.scrollTo({ left: this.slides[nextIndex].offsetLeft, behavior: 'auto' });
      this.updateControls(announce, 'auto');
      return;
    }

    this.programmaticTargetIndex = nextIndex;
    this.pendingAnnouncement = announce;
    this.track.scrollTo({ left: this.slides[nextIndex].offsetLeft, behavior });
  }

  updateControls(announce = true, thumbnailBehavior = 'smooth') {
    if (this.previousButton) this.previousButton.disabled = this.currentIndex === 0;
    if (this.nextButton) this.nextButton.disabled = this.currentIndex === this.slides.length - 1;

    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.setAttribute('aria-current', index === this.currentIndex ? 'true' : 'false');
    });

    const activeThumbnail = this.thumbnails[this.currentIndex];
    if (activeThumbnail && this.thumbnailTrack) {
      const thumbnailStart = activeThumbnail.offsetLeft;
      const thumbnailEnd = thumbnailStart + activeThumbnail.offsetWidth;
      const visibleStart = this.thumbnailTrack.scrollLeft;
      const visibleEnd = visibleStart + this.thumbnailTrack.clientWidth;

      if (thumbnailStart < visibleStart || thumbnailEnd > visibleEnd) {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.thumbnailTrack.scrollTo({
          left: thumbnailStart - (this.thumbnailTrack.clientWidth - activeThumbnail.offsetWidth) / 2,
          behavior: prefersReducedMotion ? 'auto' : thumbnailBehavior,
        });
      }
    }

    if (announce && this.status) {
      this.status.textContent = `Product image ${this.currentIndex + 1} of ${this.slides.length}`;
    }
  }
}

if (!customElements.get('starmist-product-gallery')) {
  customElements.define('starmist-product-gallery', StarmistProductGallery);
}

class StarmistStarfield extends HTMLElement {
  connectedCallback() {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onViewportChange = this.onViewportChange.bind(this);
    this.onMotionPreferenceChange = this.onMotionPreferenceChange.bind(this);
    this.reducedMotion.addEventListener?.('change', this.onMotionPreferenceChange);
    this.onMotionPreferenceChange();
  }

  disconnectedCallback() {
    this.reducedMotion?.removeEventListener?.('change', this.onMotionPreferenceChange);
    this.deactivate();
  }

  onMotionPreferenceChange() {
    if (this.reducedMotion.matches) {
      this.deactivate();
      this.replaceChildren();
      this.stars = [];
      return;
    }

    this.renderStars();
    this.activate();
  }

  renderStars() {
    if (this.childElementCount > 0) return;

    const count = Math.min(200, Math.max(20, Number(this.dataset.count) || 160));
    const speed = Math.min(30, Math.max(6, Number(this.dataset.speed) || 16));
    const maxSize = Math.min(6, Math.max(1, Number(this.dataset.maxSize) || 5));
    const parallaxDepth = Math.min(0.55, Math.max(0.05, (Number(this.dataset.parallaxDepth) || 20) / 100));
    const colors = [
      'var(--starfield-color)',
      'var(--starfield-secondary-color)',
      'var(--starfield-accent-color)',
    ];
    const random = this.createRandom(0x5a17f13d);
    const fragment = document.createDocumentFragment();
    const fieldHeight = window.innerHeight + 80;
    this.stars = [];

    for (let index = 0; index < count; index += 1) {
      const star = document.createElement('span');
      const sizeBias = random() ** 2.15;
      const size = 0.45 + sizeBias * Math.max(0.55, maxSize - 0.45);
      const duration = speed * (0.68 + random() * 0.78);
      const alpha = 0.34 + random() * 0.66;
      const shape = random();

      star.className = 'site-starfield__star';
      if (shape < 0.2) {
        star.classList.add('site-starfield__star--soft');
      } else if (shape < 0.36) {
        star.classList.add('site-starfield__star--diamond');
      } else if (shape > 0.84 && size > 1.2) {
        star.classList.add('site-starfield__star--glint');
      }

      star.style.setProperty('--star-x', `${(random() * 100).toFixed(3)}%`);
      star.style.setProperty('--star-color', colors[Math.floor(random() * colors.length)]);
      star.style.setProperty('--star-size', `${size.toFixed(2)}px`);
      star.style.setProperty('--star-alpha', alpha.toFixed(3));
      star.style.setProperty('--star-duration', `${duration.toFixed(2)}s`);
      star.style.setProperty('--star-delay', `${(-random() * duration).toFixed(2)}s`);
      fragment.append(star);
      this.stars.push({
        element: star,
        baseY: random() * fieldHeight - 40,
        depth: parallaxDepth * (0.55 + random() * 0.9),
      });
    }

    this.append(fragment);
    this.updateStarPositions();
  }

  activate() {
    window.addEventListener('scroll', this.onViewportChange, { passive: true });
    window.addEventListener('resize', this.onViewportChange);
    this.onViewportChange();
  }

  deactivate() {
    window.removeEventListener('scroll', this.onViewportChange);
    window.removeEventListener('resize', this.onViewportChange);
    if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
    this.animationFrame = null;
  }

  onViewportChange() {
    if (this.animationFrame || !this.stars?.length) return;
    this.animationFrame = requestAnimationFrame(() => {
      this.updateStarPositions();
      this.animationFrame = null;
    });
  }

  updateStarPositions() {
    const fieldHeight = window.innerHeight + 80;
    this.stars?.forEach((star) => {
      const rawY = star.baseY - window.scrollY * star.depth + 40;
      const wrappedY = ((rawY % fieldHeight) + fieldHeight) % fieldHeight - 40;
      star.element.style.setProperty('--star-y', `${wrappedY.toFixed(2)}px`);
    });
  }

  createRandom(seed) {
    let state = seed >>> 0;
    return () => {
      state += 0x6d2b79f5;
      let value = state;
      value = Math.imul(value ^ (value >>> 15), value | 1);
      value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
      return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
    };
  }
}

if (!customElements.get('starmist-starfield')) {
  customElements.define('starmist-starfield', StarmistStarfield);
}

class StarmistMagicBurst extends HTMLElement {
  connectedCallback() {
    this.anchor = this.parentElement?.querySelector('[data-magic-anchor]') || this.previousElementSibling;
    if (!this.anchor) return;

    this.count = Math.min(200, Math.max(40, Number(this.dataset.count) || 120));
    this.scrollDistance = Math.min(1200, Math.max(300, Number(this.dataset.scrollDistance) || 650));
    this.clusterSize = Math.min(12, Math.max(2, Number(this.dataset.clusterSize) || 4));
    this.lifetime = Math.min(0.45, Math.max(0.1, (Number(this.dataset.lifetime) || 30) / 100));
    this.wander = Math.min(60, Math.max(0, Number(this.dataset.wander) || 0));
    this.popSpeed = Math.min(0.3, Math.max(0.05, (Number(this.dataset.popSpeed) || 10) / 100));
    this.scintillation = Math.min(1, Math.max(0, (Number(this.dataset.scintillation) || 0) / 100));
    this.fadeInTime = Math.min(1, Math.max(0, (Number(this.dataset.fadeInTime) || 0) / 100));
    this.mixedColors = this.classList.contains('magic-burst--mixed');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mobileViewport = window.matchMedia('(max-width: 749px)');
    this.onViewportChange = this.onViewportChange.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onMotionPreferenceChange = this.onMotionPreferenceChange.bind(this);
    this.reducedMotion.addEventListener?.('change', this.onMotionPreferenceChange);
    this.mobileViewport.addEventListener?.('change', this.onMotionPreferenceChange);
    this.updateBounds();
    this.onMotionPreferenceChange();
  }

  disconnectedCallback() {
    this.reducedMotion?.removeEventListener?.('change', this.onMotionPreferenceChange);
    this.mobileViewport?.removeEventListener?.('change', this.onMotionPreferenceChange);
    this.deactivate();
  }

  onMotionPreferenceChange() {
    if (this.reducedMotion.matches || this.mobileViewport.matches) {
      this.deactivate();
      this.replaceChildren();
      this.particles = [];
    } else {
      this.updateBounds();
      this.renderParticles();
      this.activate();
    }
  }

  activate() {
    window.addEventListener('scroll', this.onViewportChange, { passive: true });
    window.addEventListener('resize', this.onResize);
    this.onViewportChange();
  }

  deactivate() {
    window.removeEventListener('scroll', this.onViewportChange);
    window.removeEventListener('resize', this.onResize);
    if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
    this.animationFrame = null;
  }

  onResize() {
    if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
    this.animationFrame = null;
    this.replaceChildren();
    this.particles = [];
    this.updateBounds();
    this.renderParticles();
    this.onViewportChange();
  }

  updateBounds() {
    const bounds = this.anchor.getBoundingClientRect();
    this.style.setProperty('--magic-anchor-width', `${bounds.width.toFixed(2)}px`);
    this.style.setProperty('--magic-anchor-height', `${bounds.height.toFixed(2)}px`);
  }

  renderParticles() {
    if (this.particles?.length) return;

    const random = this.createRandom(0x71a2c9e5 + this.count);
    const fragment = document.createDocumentFragment();
    const emissionCount = Math.ceil(this.count / this.clusterSize);
    const emissionInterval = emissionCount > 1
      ? (1 - this.lifetime) / (emissionCount - 1)
      : 0;
    const emissionJitter = Math.min(0.008, emissionInterval * 0.22);
    const mixedColorClasses = ['turquoise', 'white', 'magenta'];
    const availableWander = Math.min(this.wander, Math.max(0, this.clientWidth * 0.07));
    this.particles = [];

    for (let index = 0; index < this.count; index += 1) {
      const particleElement = document.createElement('span');
      const emission = Math.floor(index / this.clusterSize);
      const emissionStart = emission * emissionInterval;
      const delay = Math.min(
        1 - this.lifetime,
        Math.max(0, emissionStart + (random() - 0.5) * emissionJitter),
      );
      const controlAngle = random() * Math.PI * 2;
      const controlDistance = random() * availableWander;
      const endAngle = controlAngle + (random() - 0.5) * Math.PI;
      const endDistance = random() * availableWander * 0.36;
      const particle = {
        element: particleElement,
        delay,
        controlX: Math.cos(controlAngle) * controlDistance,
        controlY: Math.sin(controlAngle) * controlDistance,
        endX: Math.cos(endAngle) * endDistance,
        endY: Math.sin(endAngle) * endDistance,
        phase: random() * Math.PI * 2,
        flickerRate: 8 + random() * 12,
        scaleVariance: random() * 0.34,
      };

      particleElement.className = 'magic-burst__particle';
      if (this.mixedColors) {
        const color = mixedColorClasses[Math.floor(random() * mixedColorClasses.length)];
        particleElement.classList.add(`magic-burst__particle--${color}`);
      }

      particleElement.style.setProperty('--magic-x', `${(8 + random() * 84).toFixed(2)}%`);
      particleElement.style.setProperty('--magic-y', `${(2 + random() * 96).toFixed(2)}%`);
      particleElement.style.setProperty('--magic-size-factor', (0.52 + random() * 1.02).toFixed(3));
      particleElement.style.setProperty('--magic-radius-a', `${40 + Math.round(random() * 20)}%`);
      particleElement.style.setProperty('--magic-radius-b', `${40 + Math.round(random() * 20)}%`);
      particleElement.style.setProperty('--magic-radius-c', `${40 + Math.round(random() * 20)}%`);
      particleElement.style.setProperty('--magic-radius-d', `${40 + Math.round(random() * 20)}%`);
      fragment.append(particleElement);
      this.particles.push(particle);
    }

    this.append(fragment);
  }

  onViewportChange() {
    if (this.animationFrame || !this.particles?.length) return;
    this.animationFrame = requestAnimationFrame(() => {
      const anchorTop = window.scrollY + this.anchor.getBoundingClientRect().top;
      const startScroll = Math.max(0, anchorTop - window.innerHeight * 0.72);
      const progress = Math.min(1, Math.max(0, (window.scrollY - startScroll) / this.scrollDistance));
      const fadeProgress = this.fadeInTime === 0
        ? 1
        : Math.min(1, progress / this.fadeInTime);
      const easedFade = fadeProgress ** 2 * (3 - 2 * fadeProgress);

      this.style.setProperty('--magic-fade-progress', easedFade.toFixed(3));

      this.particles.forEach((particle) => {
        const localProgress = Math.min(1, Math.max(0, (progress - particle.delay) / this.lifetime));
        const growProgress = Math.min(1, localProgress / this.popSpeed);
        const growth = 1 - (1 - growProgress) ** 3;
        const fadeStart = Math.max(0.42, this.popSpeed + 0.12);
        const fadeProgress = Math.min(1, Math.max(0, (localProgress - fadeStart) / (1 - fadeStart)));
        const fade = fadeProgress ** 2 * (3 - 2 * fadeProgress);
        const envelope = growth * (1 - fade);
        const flickerWave = (Math.sin(localProgress * Math.PI * particle.flickerRate + particle.phase) + 1) / 2;
        const activeFlicker = 0.56 + 0.44 * flickerWave ** 2.3;
        const flicker = 1 - this.scintillation + this.scintillation * activeFlicker;
        const inverseProgress = 1 - localProgress;
        const x = 2 * inverseProgress * localProgress * particle.controlX
          + localProgress ** 2 * particle.endX;
        const y = 2 * inverseProgress * localProgress * particle.controlY
          + localProgress ** 2 * particle.endY;
        const opacity = envelope * flicker;
        const scale = (0.08 + growth * (0.96 + particle.scaleVariance))
          * (1 - fade * 0.3)
          * (0.92 + flicker * 0.12);
        const brightness = 1.38 + flicker * 1.12;

        particle.element.style.opacity = opacity.toFixed(3);
        particle.element.style.setProperty('--magic-brightness', brightness.toFixed(3));
        particle.element.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0) scale(${scale.toFixed(3)})`;
      });

      this.animationFrame = null;
    });
  }

  createRandom(seed) {
    let state = seed >>> 0;
    return () => {
      state += 0x6d2b79f5;
      let value = state;
      value = Math.imul(value ^ (value >>> 15), value | 1);
      value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
      return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
    };
  }
}

if (!customElements.get('starmist-magic-burst')) {
  customElements.define('starmist-magic-burst', StarmistMagicBurst);
}

class StarmistLightning extends HTMLElement {
  connectedCallback() {
    this.image = this.querySelector('[data-lightning-image]');
    this.ambient = this.querySelector('[data-lightning-ambient]');
    this.bolts = this.querySelector('[data-lightning-bolts]');
    if (!this.image || !this.ambient || !this.bolts) return;

    this.intensity = Math.min(1, Math.max(0.2, Number(this.dataset.intensity) || 0.75));
    this.ambientIntensity = Math.min(1, Math.max(0, Number(this.dataset.ambientIntensity) || 0));
    this.interval = Math.min(20, Math.max(2, Number(this.dataset.interval) || 8));
    this.pattern = this.dataset.pattern || 'varied';
    this.boltCount = Math.min(3, Math.max(1, Number(this.dataset.boltCount) || 2));
    this.boltSpread = Math.min(1, Math.max(0.2, (Number(this.dataset.boltSpread) || 95) / 100));
    this.boltLength = Math.min(0.7, Math.max(0.2, (Number(this.dataset.boltLength) || 40) / 100));
    const requestedBranching = Number(this.dataset.boltBranching);
    this.boltBranching = Number.isFinite(requestedBranching)
      ? Math.min(1, Math.max(0, requestedBranching / 100))
      : 0.7;
    this.boltChaos = Math.min(1, Math.max(0.1, (Number(this.dataset.boltChaos) || 60) / 100));
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mobileViewport = window.matchMedia('(max-width: 749px)');
    this.onMotionPreferenceChange = this.onMotionPreferenceChange.bind(this);
    this.reducedMotion.addEventListener?.('change', this.onMotionPreferenceChange);
    this.mobileViewport.addEventListener?.('change', this.onMotionPreferenceChange);
    this.onMotionPreferenceChange();
  }

  disconnectedCallback() {
    this.reducedMotion?.removeEventListener?.('change', this.onMotionPreferenceChange);
    this.mobileViewport?.removeEventListener?.('change', this.onMotionPreferenceChange);
    this.enabled = false;
    this.stop();
  }

  onMotionPreferenceChange() {
    this.enabled = !this.reducedMotion.matches && !this.mobileViewport.matches;
    this.stop();
    if (this.enabled) this.schedule(true);
  }

  stop() {
    window.clearTimeout(this.timer);
    this.timer = null;
    this.animations?.forEach((animation) => animation.cancel());
    this.animations = [];
    this.bolts?.replaceChildren();
  }

  schedule(initial = false) {
    if (!this.enabled) return;
    const delay = initial
      ? 900 + Math.random() * 1700
      : this.interval * 1000 * (0.55 + Math.random() * 0.9);
    this.timer = window.setTimeout(() => this.strike(), delay);
  }

  strike() {
    if (!this.enabled) return;

    let pulseCount = 1;
    if (this.pattern === 'double') pulseCount = 2;
    if (this.pattern === 'varied') pulseCount = 1 + Math.floor(Math.random() * 3);

    const pulseOffsets = pulseCount === 1
      ? [0.16]
      : pulseCount === 2
        ? [0.1, 0.52]
        : [0.08, 0.36, 0.7];
    const imageFrames = [{ opacity: 0, offset: 0 }];
    const ambientFrames = [{ opacity: 0, offset: 0 }];
    const strike = this.createBolts();

    this.style.setProperty('--lightning-active-x', `${strike.centerX.toFixed(2)}%`);
    this.style.setProperty('--lightning-active-y', `${strike.centerY.toFixed(2)}%`);

    pulseOffsets.forEach((offset, index) => {
      const amplitude = this.intensity * (0.72 + Math.random() * 0.28) * (index === 0 ? 1 : 0.84);
      const ambientAmplitude = this.ambientIntensity * (0.62 + Math.random() * 0.28);
      imageFrames.push(
        { opacity: 0, offset: Math.max(0, offset - 0.018) },
        { opacity: amplitude, offset },
        { opacity: amplitude * 0.18, offset: Math.min(0.96, offset + 0.055) },
        { opacity: 0, offset: Math.min(0.98, offset + 0.12) },
      );
      ambientFrames.push(
        { opacity: 0, offset: Math.max(0, offset - 0.025) },
        { opacity: ambientAmplitude, offset },
        { opacity: ambientAmplitude * 0.22, offset: Math.min(0.96, offset + 0.1) },
        { opacity: 0, offset: Math.min(0.98, offset + 0.2) },
      );
    });

    imageFrames.push({ opacity: 0, offset: 1 });
    ambientFrames.push({ opacity: 0, offset: 1 });
    imageFrames.sort((first, second) => first.offset - second.offset);
    ambientFrames.sort((first, second) => first.offset - second.offset);

    const duration = 260 + pulseCount * 150 + Math.random() * 90;
    const boltFrames = [{ opacity: 0, strokeDashoffset: 1, offset: 0 }];
    pulseOffsets.forEach((offset, index) => {
      const pulseOpacity = this.intensity * (index === 0 ? 1 : 0.76);
      boltFrames.push(
        { opacity: 0, strokeDashoffset: index === 0 ? 1 : 0, offset: Math.max(0, offset - 0.018) },
        { opacity: pulseOpacity, strokeDashoffset: 0, offset },
        { opacity: pulseOpacity * 0.2, strokeDashoffset: 0, offset: Math.min(0.96, offset + 0.055) },
        { opacity: 0, strokeDashoffset: 0, offset: Math.min(0.98, offset + 0.12) },
      );
    });
    boltFrames.push({ opacity: 0, strokeDashoffset: 0, offset: 1 });
    boltFrames.sort((first, second) => first.offset - second.offset);

    this.animations = [
      this.image.animate(imageFrames, { duration, easing: 'linear' }),
      this.ambient.animate(ambientFrames, {
        duration: duration * 1.08,
        easing: 'cubic-bezier(0.18, 0.72, 0.24, 1)',
      }),
      ...strike.paths.map((path, index) => path.animate(boltFrames, {
        duration,
        delay: index * 5,
        easing: 'linear',
      })),
    ];

    Promise.allSettled(this.animations.map((animation) => animation.finished)).then(() => {
      this.animations = [];
      this.bolts.replaceChildren();
      if (this.enabled) this.schedule();
    });
  }

  createBolts() {
    const bounds = this.getBoundingClientRect();
    const width = Math.max(1, bounds.width);
    const height = Math.max(1, bounds.height);
    const fieldCenter = Number.parseFloat(
      getComputedStyle(this).getPropertyValue('--lightning-field-x'),
    ) || 50;
    const halfSpread = this.boltSpread * 50;
    const minimumX = Math.max(4, fieldCenter - halfSpread);
    const maximumX = Math.min(96, fieldCenter + halfSpread);
    const activeBoltCount = 1 + Math.floor(Math.random() * this.boltCount);
    const paths = [];
    const centers = [];

    this.bolts.replaceChildren();
    this.bolts.setAttribute('viewBox', `0 0 ${width.toFixed(2)} ${height.toFixed(2)}`);

    for (let boltIndex = 0; boltIndex < activeBoltCount; boltIndex += 1) {
      const start = {
        x: width * (minimumX + Math.random() * (maximumX - minimumX)) / 100,
        y: height * (0.025 + Math.random() * 0.12),
      };
      const end = {
        x: Math.min(width * 0.96, Math.max(width * 0.04, start.x + (Math.random() - 0.5) * width * 0.24)),
        y: Math.min(height * 0.76, start.y + height * this.boltLength * (0.72 + Math.random() * 0.48)),
      };
      const distance = Math.hypot(end.x - start.x, end.y - start.y);
      const segmentCount = Math.min(36, Math.max(18, Math.round(distance / 12)));
      const chaos = Math.min(width, height) * 0.05 * this.boltChaos;
      const points = this.createBoltPoints(start, end, segmentCount, chaos);

      this.appendBoltPath(points, paths, false);
      centers.push({ x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 });

      const branchCount = Math.min(5, Math.floor(this.boltBranching * 5 + Math.random()));
      for (let branchIndex = 0; branchIndex < branchCount; branchIndex += 1) {
        const pointIndex = Math.min(
          points.length - 3,
          Math.max(2, 2 + Math.floor(Math.random() * Math.max(1, points.length - 5))),
        );
        const branchStart = points[pointIndex];
        const previousPoint = points[pointIndex - 1];
        const tangent = Math.atan2(
          branchStart.y - previousPoint.y,
          branchStart.x - previousPoint.x,
        );
        const direction = Math.random() > 0.5 ? 1 : -1;
        const angle = tangent + direction * (0.48 + Math.random() * 0.72);
        const branchLength = distance * (0.18 + Math.random() * 0.28);
        const branchEnd = {
          x: branchStart.x + Math.cos(angle) * branchLength,
          y: branchStart.y + Math.sin(angle) * branchLength,
        };
        const branchPoints = this.createBoltPoints(
          branchStart,
          branchEnd,
          7 + Math.floor(Math.random() * 6),
          chaos * 0.68,
        );
        this.appendBoltPath(branchPoints, paths, true);
      }
    }

    const center = centers.reduce(
      (total, point) => ({ x: total.x + point.x, y: total.y + point.y }),
      { x: 0, y: 0 },
    );

    return {
      paths,
      centerX: center.x / centers.length / width * 100,
      centerY: center.y / centers.length / height * 100,
    };
  }

  createBoltPoints(start, end, segmentCount, chaos) {
    const iterations = Math.min(6, Math.max(3, Math.ceil(Math.log2(segmentCount))));
    let displacement = chaos;
    let points = [start, end];

    for (let iteration = 0; iteration < iterations; iteration += 1) {
      const subdivided = [points[0]];

      for (let index = 0; index < points.length - 1; index += 1) {
        const first = points[index];
        const second = points[index + 1];
        const deltaX = second.x - first.x;
        const deltaY = second.y - first.y;
        const distance = Math.max(1, Math.hypot(deltaX, deltaY));
        const perpendicularX = -deltaY / distance;
        const perpendicularY = deltaX / distance;
        const perpendicularOffset = (Math.random() * 2 - 1) * displacement;
        const alongOffset = (Math.random() - 0.5) * displacement * 0.2;
        const midpoint = {
          x: (first.x + second.x) / 2
            + perpendicularX * perpendicularOffset
            + deltaX / distance * alongOffset,
          y: (first.y + second.y) / 2
            + perpendicularY * perpendicularOffset
            + deltaY / distance * alongOffset,
        };

        subdivided.push(midpoint, second);
      }

      points = subdivided;
      displacement *= 0.54;
    }

    return points;
  }

  appendBoltPath(points, paths, isBranch) {
    const pathData = points
      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
      .join(' ');

    ['glow', 'core'].forEach((layer) => {
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('class', `image-banner__lightning-bolt image-banner__lightning-bolt--${layer}`);
      if (isBranch) path.classList.add('image-banner__lightning-bolt--branch');
      path.setAttribute('d', pathData);
      path.setAttribute('pathLength', '1');
      this.bolts.append(path);
      paths.push(path);
    });
  }
}

if (!customElements.get('starmist-lightning')) {
  customElements.define('starmist-lightning', StarmistLightning);
}

class StarmistParallaxHero {
  constructor(element) {
    this.element = element;
    this.strength = Math.min(0.5, Math.max(0.1, Number(element.dataset.parallaxStrength) || 0.2));
    this.duration = Math.min(1, Math.max(0.25, Number(element.dataset.parallaxDuration) || 1));
    this.sectionWrapper = element.closest('.shopify-section') || element.parentElement;
    this.followingSection = this.sectionWrapper?.nextElementSibling;
    this.cloudStream = this.sectionWrapper?.querySelector('[data-hero-clouds]');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onViewportChange = this.onViewportChange.bind(this);
    this.onMotionPreferenceChange = this.onMotionPreferenceChange.bind(this);
    this.onIntersection = this.onIntersection.bind(this);
    this.active = false;

    this.observer = new IntersectionObserver(this.onIntersection, { rootMargin: '15% 0px' });
    this.observer.observe(this.element);
    this.reducedMotion.addEventListener?.('change', this.onMotionPreferenceChange);
    this.onMotionPreferenceChange();
  }

  destroy() {
    this.observer?.disconnect();
    this.reducedMotion.removeEventListener?.('change', this.onMotionPreferenceChange);
    this.stopTracking();
    this.element.style.removeProperty('--image-banner-parallax-offset');
    this.cloudStream?.style.removeProperty('--image-banner-parallax-offset');
    delete this.element.starmistParallaxHero;
  }

  onIntersection(entries) {
    this.active = entries.some((entry) => entry.isIntersecting);
    if (this.active && !this.reducedMotion.matches) {
      this.startTracking();
    } else {
      this.stopTracking();
    }
  }

  onMotionPreferenceChange() {
    if (this.reducedMotion.matches) {
      this.stopTracking();
      this.element.style.setProperty('--image-banner-parallax-offset', '0px');
      this.cloudStream?.style.setProperty('--image-banner-parallax-offset', '0px');
    } else if (this.active) {
      this.startTracking();
    }
  }

  startTracking() {
    window.addEventListener('scroll', this.onViewportChange, { passive: true });
    window.addEventListener('resize', this.onViewportChange);
    this.onViewportChange();
  }

  stopTracking() {
    window.removeEventListener('scroll', this.onViewportChange);
    window.removeEventListener('resize', this.onViewportChange);
    if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
    this.animationFrame = null;
  }

  onViewportChange() {
    if (this.animationFrame) return;
    this.animationFrame = requestAnimationFrame(() => {
      const bounds = this.element.getBoundingClientRect();
      const followingBounds = this.followingSection?.getBoundingClientRect();
      const distancePastTop = Math.max(0, -bounds.top);
      const distanceUntilFollowingSection = followingBounds
        ? Math.max(1, followingBounds.top - bounds.top)
        : bounds.height;
      const movementDistance = distanceUntilFollowingSection * this.duration;
      const offset = Math.min(distancePastTop, movementDistance) * this.strength;

      const offsetValue = `${offset.toFixed(2)}px`;
      this.element.style.setProperty('--image-banner-parallax-offset', offsetValue);
      this.cloudStream?.style.setProperty('--image-banner-parallax-offset', offsetValue);
      this.animationFrame = null;
    });
  }
}

function initializeParallaxHeroes(root = document) {
  const heroes = [];
  if (root.matches?.('[data-parallax-hero]')) heroes.push(root);
  heroes.push(...root.querySelectorAll?.('[data-parallax-hero]') || []);

  heroes.forEach((hero) => {
    if (hero.starmistParallaxHero) return;
    hero.starmistParallaxHero = new StarmistParallaxHero(hero);
  });
}

initializeParallaxHeroes();

document.addEventListener('shopify:section:load', (event) => initializeParallaxHeroes(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll?.('[data-parallax-hero]').forEach((hero) => hero.starmistParallaxHero?.destroy());
});

class StarmistHeader extends HTMLElement {
  connectedCallback() {
    this.onKeydown = this.onKeydown.bind(this);
    this.updateVisualState = this.updateVisualState.bind(this);
    this.updateOverlayHeight = this.updateOverlayHeight.bind(this);
    this.addEventListener('keydown', this.onKeydown);
    window.addEventListener('scroll', this.updateVisualState, { passive: true });
    window.addEventListener('resize', this.updateOverlayHeight);

    this.resizeObserver = new ResizeObserver(this.updateOverlayHeight);
    this.resizeObserver.observe(this);
    this.updateVisualState();
    this.updateOverlayHeight();
  }

  disconnectedCallback() {
    this.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('scroll', this.updateVisualState);
    window.removeEventListener('resize', this.updateOverlayHeight);
    this.resizeObserver?.disconnect();
  }

  updateVisualState() {
    this.classList.toggle('site-header--scrolled', window.scrollY > 24);
  }

  updateOverlayHeight() {
    document.documentElement.style.setProperty('--header-overlay-height', `${this.offsetHeight}px`);
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;

    const disclosure = event.target.closest('details[open]');
    if (!disclosure || !this.contains(disclosure)) return;

    event.preventDefault();
    disclosure.removeAttribute('open');
    disclosure.querySelector(':scope > summary')?.focus();
  }
}

if (!customElements.get('starmist-header')) {
  customElements.define('starmist-header', StarmistHeader);
}

class StarmistLocalization extends HTMLElement {
  connectedCallback() {
    this.onKeydown = this.onKeydown.bind(this);
    this.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('keydown', this.onKeydown);
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;

    const disclosure = event.target.closest('details[open]');
    if (!disclosure || !this.contains(disclosure)) return;

    event.preventDefault();
    disclosure.removeAttribute('open');
    disclosure.querySelector(':scope > summary')?.focus();
  }
}

if (!customElements.get('starmist-localization')) {
  customElements.define('starmist-localization', StarmistLocalization);
}

class StarmistCollection extends HTMLElement {
  connectedCallback() {
    this.onKeydown = this.onKeydown.bind(this);
    this.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('keydown', this.onKeydown);
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;

    const disclosure = event.target.closest('details[open]');
    if (!disclosure || !this.contains(disclosure)) return;

    event.preventDefault();
    disclosure.removeAttribute('open');
    disclosure.querySelector(':scope > summary')?.focus();
  }
}

if (!customElements.get('starmist-collection')) {
  customElements.define('starmist-collection', StarmistCollection);
}

class StarmistProductDetail extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector(':scope > details');
    this.summary = this.details?.querySelector(':scope > summary');
    this.content = this.details?.querySelector(':scope > .product__detail-content');
    if (!this.details || !this.summary || !this.content) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.details.dataset.detailState = this.details.open ? 'open' : 'closed';
    this.onSummaryClick = this.onSummaryClick.bind(this);
    this.onTransitionEnd = this.onTransitionEnd.bind(this);
    this.summary.addEventListener('click', this.onSummaryClick);
    this.content.addEventListener('transitionend', this.onTransitionEnd);
  }

  disconnectedCallback() {
    this.summary?.removeEventListener('click', this.onSummaryClick);
    this.content?.removeEventListener('transitionend', this.onTransitionEnd);
    if (this.animationTimer) window.clearTimeout(this.animationTimer);
  }

  onSummaryClick(event) {
    event.preventDefault();
    const state = this.details.dataset.detailState;
    const shouldOpen = !this.details.open || state === 'closing';
    this.setExpanded(shouldOpen);
  }

  setExpanded(expanded) {
    if (this.animationTimer) window.clearTimeout(this.animationTimer);
    this.targetExpanded = expanded;

    if (this.reducedMotion.matches) {
      this.finishTransition(expanded);
      return;
    }

    if (expanded && !this.details.open) {
      this.details.open = true;
      this.details.dataset.detailState = 'closed';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.startTransition(true));
      });
      return;
    }

    this.startTransition(expanded);
  }

  startTransition(expanded) {
    if (this.targetExpanded !== expanded) return;
    this.details.dataset.detailState = expanded ? 'opening' : 'closing';
    this.animationTimer = window.setTimeout(() => this.finishTransition(expanded), 560);
  }

  onTransitionEnd(event) {
    if (event.target !== this.content || event.propertyName !== 'grid-template-rows') return;
    this.finishTransition(this.targetExpanded);
  }

  finishTransition(expanded) {
    if (this.animationTimer) window.clearTimeout(this.animationTimer);
    this.animationTimer = null;
    this.targetExpanded = expanded;
    this.details.open = expanded;
    this.details.dataset.detailState = expanded ? 'open' : 'closed';
  }
}

if (!customElements.get('starmist-product-detail')) {
  customElements.define('starmist-product-detail', StarmistProductDetail);
}

class StarmistExcerptDisclosure extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector(':scope > details');
    this.summary = this.details?.querySelector(':scope > summary');
    this.content = this.details?.querySelector(':scope > .product__detail-content--excerpt');
    if (!this.details || !this.summary || !this.content) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.details.dataset.excerptState = this.details.open ? 'open' : 'closed';
    this.onSummaryClick = this.onSummaryClick.bind(this);
    this.onTransitionEnd = this.onTransitionEnd.bind(this);
    this.summary.addEventListener('click', this.onSummaryClick);
    this.content.addEventListener('transitionend', this.onTransitionEnd);
  }

  disconnectedCallback() {
    this.summary?.removeEventListener('click', this.onSummaryClick);
    this.content?.removeEventListener('transitionend', this.onTransitionEnd);
    if (this.animationTimer) window.clearTimeout(this.animationTimer);
  }

  onSummaryClick(event) {
    event.preventDefault();
    const state = this.details.dataset.excerptState;
    const shouldOpen = !this.details.open || state === 'closing';
    this.setExpanded(shouldOpen);
  }

  setExpanded(expanded) {
    if (this.animationTimer) window.clearTimeout(this.animationTimer);
    this.targetExpanded = expanded;

    if (this.reducedMotion.matches) {
      this.finishTransition(expanded);
      return;
    }

    if (expanded && !this.details.open) {
      this.details.open = true;
      this.details.dataset.excerptState = 'closed';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.startTransition(true));
      });
      return;
    }

    this.startTransition(expanded);
  }

  startTransition(expanded) {
    if (this.targetExpanded !== expanded) return;
    this.details.dataset.excerptState = expanded ? 'opening' : 'closing';
    this.animationTimer = window.setTimeout(() => this.finishTransition(expanded), expanded ? 850 : 600);
  }

  onTransitionEnd(event) {
    if (event.target !== this.content || event.propertyName !== 'grid-template-rows') return;
    this.finishTransition(this.targetExpanded);
  }

  finishTransition(expanded) {
    if (this.animationTimer) window.clearTimeout(this.animationTimer);
    this.animationTimer = null;
    this.targetExpanded = expanded;
    this.details.open = expanded;
    this.details.dataset.excerptState = expanded ? 'open' : 'closed';

    if (!expanded && this.returnToSummary) {
      this.returnToSummary = false;
      this.summary.focus({ preventScroll: true });
      this.summary.scrollIntoView({
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
        block: 'center',
      });
    }
  }

  closeFromToolbar() {
    this.returnToSummary = true;
    this.summary.focus({ preventScroll: true });
    this.setExpanded(false);
  }
}

if (!customElements.get('starmist-excerpt-disclosure')) {
  customElements.define('starmist-excerpt-disclosure', StarmistExcerptDisclosure);
}

class StarmistExcerptReader extends HTMLElement {
  connectedCallback() {
    this.decreaseButton = this.querySelector('[data-reader-decrease]');
    this.increaseButton = this.querySelector('[data-reader-increase]');
    this.narrowButton = this.querySelector('[data-reader-narrow]');
    this.widenButton = this.querySelector('[data-reader-widen]');
    this.closeButton = this.querySelector('[data-reader-close]');
    this.status = this.querySelector('[data-reader-status]');
    this.fontSize = Math.min(26, Math.max(16, Number(this.dataset.fontSize) || 19));
    this.measure = Math.min(100, Math.max(44, Number(this.dataset.readerMeasure) || 68));
    this.measureIsFull = false;
    this.onCloseClick = this.onCloseClick.bind(this);

    this.decreaseButton?.addEventListener('click', () => this.setFontSize(this.fontSize - 1));
    this.increaseButton?.addEventListener('click', () => this.setFontSize(this.fontSize + 1));
    this.narrowButton?.addEventListener('click', () => this.setMeasure(this.measureIsFull ? 100 : this.measure - 8));
    this.widenButton?.addEventListener('click', () => {
      if (this.measure >= 100) {
        this.setMeasure('full');
        return;
      }

      this.setMeasure(this.measure + 8);
    });
    this.closeButton?.addEventListener('click', this.onCloseClick);
    this.setFontSize(this.fontSize, false);
    this.setMeasure(this.measure, false);
  }

  disconnectedCallback() {
    this.closeButton?.removeEventListener('click', this.onCloseClick);
  }

  onCloseClick() {
    this.closest('starmist-excerpt-disclosure')?.closeFromToolbar();
  }

  setFontSize(fontSize, announce = true) {
    this.fontSize = Math.min(26, Math.max(16, fontSize));
    this.style.setProperty('--reader-font-size', `${this.fontSize}px`);

    if (this.decreaseButton) this.decreaseButton.disabled = this.fontSize <= 16;
    if (this.increaseButton) this.increaseButton.disabled = this.fontSize >= 26;
    if (announce && this.status) this.status.textContent = `Excerpt text size ${this.fontSize} pixels`;
  }

  setMeasure(measure, announce = true) {
    if (measure === 'full') {
      this.measureIsFull = true;
      this.style.setProperty('--reader-measure', '100%');
    } else {
      this.measureIsFull = false;
      this.measure = Math.min(100, Math.max(44, Number(measure) || 68));
      this.style.setProperty('--reader-measure', `${this.measure}ch`);
    }

    if (this.narrowButton) this.narrowButton.disabled = !this.measureIsFull && this.measure <= 44;
    if (this.widenButton) this.widenButton.disabled = this.measureIsFull;

    if (announce && this.status) {
      this.status.textContent = this.measureIsFull
        ? 'Excerpt text column full width'
        : `Excerpt text column ${this.measure} characters wide`;
    }
  }
}

if (!customElements.get('starmist-excerpt-reader')) {
  customElements.define('starmist-excerpt-reader', StarmistExcerptReader);
}

class StarmistShare extends HTMLElement {
  connectedCallback() {
    this.instagramButton = this.querySelector('[data-share-instagram]');
    this.copyButton = this.querySelector('[data-share-copy]');
    this.status = this.querySelector('[data-share-status]');
    this.onInstagramClick = () => this.shareToInstagram();
    this.onCopyClick = () => this.copyLink();
    this.instagramButton?.addEventListener('click', this.onInstagramClick);
    this.copyButton?.addEventListener('click', this.onCopyClick);
  }

  disconnectedCallback() {
    this.instagramButton?.removeEventListener('click', this.onInstagramClick);
    this.copyButton?.removeEventListener('click', this.onCopyClick);
  }

  async writeShareUrl() {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(this.dataset.shareUrl);
      return;
    }

    const textArea = document.createElement('textarea');
    textArea.value = this.dataset.shareUrl;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.append(textArea);
    textArea.select();
    document.execCommand('copy');
    textArea.remove();
  }

  async shareToInstagram() {
    const shareData = {
      title: this.dataset.shareTitle || document.title,
      url: this.dataset.shareUrl,
    };

    if (typeof navigator.share === 'function' && (!navigator.canShare || navigator.canShare(shareData))) {
      try {
        await navigator.share(shareData);
        if (this.status) this.status.textContent = 'Product shared.';
        return;
      } catch (error) {
        if (error.name === 'AbortError') return;
      }
    }

    window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');

    try {
      await this.writeShareUrl();
      if (this.status) this.status.textContent = 'Product link copied. Paste it into Instagram to share.';
    } catch (error) {
      if (this.status) this.status.textContent = 'Instagram opened. Copy this page address to share it.';
    }
  }

  async copyLink() {
    try {
      await this.writeShareUrl();

      if (this.status) this.status.textContent = 'Product link copied.';
    } catch (error) {
      if (this.status) this.status.textContent = 'Unable to copy the link.';
    }
  }
}

if (!customElements.get('starmist-share')) {
  customElements.define('starmist-share', StarmistShare);
}

class StarmistStickyProduct extends HTMLElement {
  connectedCallback() {
    this.sourceButton = this.closest('starmist-product')?.querySelector('[data-primary-add-to-cart]');
    if (!this.sourceButton) return;

    this.updateVisibility = this.updateVisibility.bind(this);
    window.addEventListener('scroll', this.updateVisibility, { passive: true });
    window.addEventListener('resize', this.updateVisibility);
    this.updateVisibility();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.updateVisibility);
    window.removeEventListener('resize', this.updateVisibility);
    document.body.classList.remove('has-sticky-product');
  }

  updateVisibility() {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const hasPassedPurchaseButton = this.sourceButton.getBoundingClientRect().bottom < 0;
    const shouldShow = isMobile && hasPassedPurchaseButton;

    this.hidden = !shouldShow;
    document.body.classList.toggle('has-sticky-product', shouldShow);
  }
}

if (!customElements.get('starmist-sticky-product')) {
  customElements.define('starmist-sticky-product', StarmistStickyProduct);
}

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url || this.dataset.loaded === 'true') return;

    this.dataset.loaded = 'true';

    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const documentFragment = new DOMParser().parseFromString(text, 'text/html');
        const recommendations = documentFragment.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim()) {
          this.innerHTML = recommendations.innerHTML;
        }
      })
      .catch(() => {
        this.dataset.loaded = 'false';
      });
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}

class StarmistPredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-predictive-input]');
    this.results = this.querySelector('[data-predictive-results]');
    this.disclosure = this.closest('details');
    this.abortController = null;
    this.debounceTimer = null;

    if (!this.input || !this.results || !this.dataset.url) return;

    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        this.disclosure?.removeAttribute('open');
        this.disclosure?.querySelector(':scope > summary')?.focus();
      }
    });

    this.onDisclosureToggle = () => {
      if (this.disclosure?.open) {
        window.requestAnimationFrame(() => this.input.focus());
      } else {
        this.close();
      }
    };
    this.disclosure?.addEventListener('toggle', this.onDisclosureToggle);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    window.clearTimeout(this.debounceTimer);
    this.disclosure?.removeEventListener('toggle', this.onDisclosureToggle);
  }

  onInput() {
    window.clearTimeout(this.debounceTimer);

    const query = this.input.value.trim();
    if (query.length < 2) {
      this.close();
      return;
    }

    this.debounceTimer = window.setTimeout(() => this.getSearchResults(query), 200);
  }

  async getSearchResults(query) {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.results.setAttribute('aria-busy', 'true');

    const searchUrl = new URL(this.dataset.url, window.location.origin);
    searchUrl.searchParams.set('q', query);
    searchUrl.searchParams.set('section_id', 'predictive-search');
    searchUrl.searchParams.set('resources[type]', 'product,collection,page,article,query');
    searchUrl.searchParams.set('resources[limit]', '6');
    searchUrl.searchParams.set('resources[options][unavailable_products]', 'last');

    try {
      const response = await fetch(searchUrl, { signal: this.abortController.signal });
      if (!response.ok) throw new Error('Predictive search request failed');

      const text = await response.text();
      const responseDocument = new DOMParser().parseFromString(text, 'text/html');
      const content = responseDocument.querySelector('[data-predictive-content]');

      this.results.innerHTML = content?.innerHTML || '<p>No suggestions found.</p>';
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    } catch (error) {
      if (error.name !== 'AbortError') this.close();
    } finally {
      this.results.removeAttribute('aria-busy');
    }
  }

  close() {
    this.abortController?.abort();
    this.results.hidden = true;
    this.results.innerHTML = '';
    this.input.setAttribute('aria-expanded', 'false');
  }
}

if (!customElements.get('starmist-predictive-search')) {
  customElements.define('starmist-predictive-search', StarmistPredictiveSearch);
}

class StarmistScroller extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-scroller-track]');
    this.controls = this.querySelector('[data-scroller-controls]');
    this.previousButton = this.querySelector('[data-scroller-previous]');
    this.nextButton = this.querySelector('[data-scroller-next]');

    if (!this.track || !this.previousButton || !this.nextButton) return;

    this.previousButton.addEventListener('click', () => this.scroll(-1));
    this.nextButton.addEventListener('click', () => this.scroll(1));
    this.track.addEventListener('scroll', () => this.updateControls(), { passive: true });

    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    this.resizeObserver.observe(this.track);
    this.updateControls();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  scroll(direction) {
    const distance = Math.max(280, this.track.clientWidth * 0.85);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.track.scrollBy({
      left: direction * distance,
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  }

  updateControls() {
    const maximumScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    if (this.controls) this.controls.hidden = maximumScroll <= 1;
    this.previousButton.disabled = this.track.scrollLeft <= 1;
    this.nextButton.disabled = this.track.scrollLeft >= maximumScroll - 1;
  }
}

if (!customElements.get('starmist-scroller')) {
  customElements.define('starmist-scroller', StarmistScroller);
}

class StarmistCountdown extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.endDate) return;

    const endTime = this.dataset.endTime || '23:59:59';
    this.endDate = new Date(`${this.dataset.endDate}T${endTime}`);
    if (Number.isNaN(this.endDate.getTime())) return;

    this.update();
    this.interval = window.setInterval(() => this.update(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.interval);
  }

  update() {
    const remaining = this.endDate.getTime() - Date.now();

    if (remaining <= 0) {
      window.clearInterval(this.interval);
      this.renderValue('days', 0);
      this.renderValue('hours', 0);
      this.renderValue('minutes', 0);
      this.renderValue('seconds', 0);

      const completeMessage = this.querySelector('[data-countdown-complete]');
      if (completeMessage) completeMessage.hidden = false;
      if (this.dataset.hideAfterEnd === 'true') this.closest('.countdown-timer').hidden = true;
      return;
    }

    const days = Math.floor(remaining / 86400000);
    const hours = Math.floor((remaining % 86400000) / 3600000);
    const minutes = Math.floor((remaining % 3600000) / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);

    this.renderValue('days', days);
    this.renderValue('hours', hours);
    this.renderValue('minutes', minutes);
    this.renderValue('seconds', seconds);
    this.setAttribute('aria-label', `${days} days, ${hours} hours, ${minutes} minutes remaining`);
  }

  renderValue(unit, value) {
    const element = this.querySelector(`[data-${unit}]`);
    if (element) element.textContent = String(value).padStart(2, '0');
  }
}

if (!customElements.get('starmist-countdown')) {
  customElements.define('starmist-countdown', StarmistCountdown);
}
